<template lang="pug">
.tooltip-content-container
  .top
    .panel.assigned-agent-container
      .header
        font-awesome-icon(:icon="['fas', 'user-group']")
        span {{ $t(`${baseTranslationPath}.assigned_agent`) }}
      .field-group
        .field-label {{ $t(`${baseTranslationPath}.assigned_agent`) }}
        .no-agents(v-if="!primaryAgent && !assignedAgents.length") {{ $t(`${baseTranslationPath}.no_agents`) }}
        template(v-else)
          .agent-row(v-if="primaryAgent")
            aiq-image.agent-avatar(:src="agentAvatarUrl(primaryAgent)" :key="primaryAgent.id")
            span.agent-name {{ primaryAgent.fullName }}
            span.agent-role ({{ $t(`${baseTranslationPath}.primary_agent`) }})
          .agent-row(v-for="agent in assignedAgents" :key="agent.id")
            aiq-image.agent-avatar(:src="agentAvatarUrl(agent)" :key="agent.id")
            span.agent-name {{ agent.fullName }}
      .lock-status(:class="{ unlocked: !lockStatus.isLocked }")
        .lock-status-title
          span {{ lockStatus.title }}
          font-awesome-icon(:icon="['fas', lockStatus.icon]")
        .lock-status-line(v-if="lockStatus.message") {{ lockStatus.message }}
        .lock-status-line(v-if="lockStatus.at") on {{ lockStatus.at }}
    .panel.activity-container
      .header
        font-awesome-icon(:icon="['fas', 'clock']")
        span {{ $t(`${baseTranslationPath}.activity`) }}
      .field-group
        .field-label {{ $t(`${baseTranslationPath}.last_sender`) }}
        .agent-row(v-if="lastSenderInfo")
          aiq-image.agent-avatar(v-if="lastSenderInfo.avatarUrl" :src="lastSenderInfo.avatarUrl" :key="lastSenderInfo.avatarKey")
          span.agent-avatar.sender-avatar-icon(v-else)
            font-awesome-icon(:icon="['fas', 'circle-user']")
          span.agent-name {{ lastSenderInfo.name }}
          span.agent-role(v-if="lastSenderInfo.role") ({{ lastSenderInfo.role }})
        .muted-value(v-else) N/A
      .field-group
        .field-label {{ $t(`${baseTranslationPath}.last_updated`) }}
        .field-value.last-updated {{ lastUpdated }}
      .status-row
        span.field-label {{ $t(`${baseTranslationPath}.status`) }}
        span.field-value.status-badge(:class="{ inactive: !isActive }") {{ statusLabel }}
  .conversation-context-container
    .header
      font-awesome-icon(:icon="['fas', 'message-dots']")
      span {{ $t(`${baseTranslationPath}.conversation_context`) }}
    .field-group
      .field-label {{ $t(`${baseTranslationPath}.last_message_preview`) }}
      .last-message-preview(v-if="shouldRenderLatestMessageHtml")
        .field-value.latest-message(v-html="lastMessagePreview.content")
        .last-message-attachment-count(v-if="lastMessagePreview.imageCount")
          font-awesome-icon(:icon="['fas', 'image']")
          span {{ lastMessagePreview.imageCount }}
      .field-value(v-else-if="shouldRenderLatestMessageReplies")
        .latest-message-reply-content(v-if="latestMessageRepliesContent" v-html="$filters.sanitize(latestMessageRepliesContent)")
        .latest-message-replies(v-if="latestMessageReplies.length")
          span.latest-message-reply(v-for="reply in latestMessageReplies" :key="reply") {{ reply }}
      .field-value(v-else-if="shouldRenderLatestMessageSelectedReply")
        .latest-message-reply-content(v-if="latestMessageSelectedReplyContent" v-html="$filters.sanitize(latestMessageSelectedReplyContent)")
        .latest-message-replies(v-if="latestMessageSelectedReply.length")
          span.latest-message-reply(v-for="reply in latestMessageSelectedReply" :key="reply") {{ reply }}
      .field-value.latest-message-call(v-else-if="shouldRenderLatestMessageCall") {{ latestMessageCall }}
      .field-value.latest-message-carousel(v-else-if="shouldRenderLatestMessageCarousel") {{ latestMessageCarousel }}
      .field-value.latest-message-rich-text(v-else-if="shouldRenderLatestMessageRichText")
        .last-message-preview
          span.rich-text-content(v-html="latestMessageRichTextContent.content")
          .last-message-attachment-count(v-if="latestMessageRichTextAttachments.count")
            font-awesome-icon(:icon="['fas', latestMessageRichTextAttachments.icon]")
            span {{ latestMessageRichTextAttachments.count }}
      message.tooltip-content-message(v-else :message="latestMessage")
    .field-group
      .field-label {{ $t(`${baseTranslationPath}.summary`) }}
      .field-value.summary-text(:class="{ 'summary-unavailable': !summary }") {{ summary || $t(`${baseTranslationPath}.summary_unavailable`) }}
    .field-group(v-if="tags.length")
      .field-label {{ $t(`${baseTranslationPath}.tags`) }}
      .tags-list
        span.tag(v-for="tag in tags" :key="tag.id") {{ tag.name }}

</template>

<script>
import dayjs from '@/localPlugins/dayjs';
import get from 'lodash/get';
import has from 'lodash/has';
import isUndefined from 'lodash/isUndefined';
import upperFirst from 'lodash/upperFirst';
import GetGlobalSource from '@/libs/mixins/GetGlobalSource';
import { DEFAULT_PICTURE_URL } from '@/constants';
import { BOT_EMAIL } from '@/constants/agent';

export default {
  name: 'TooltipContent',
  props: {
    shallowConversationData: {
      type: Object,
      required: true,
    },
    deepConversationData: {
      type: Object,
      required: true,
    },
  },
  // Need to add GetGlobalSource mixin because it's a dependency in message in aiq-components
  mixins: [GetGlobalSource],
  data() {
    return {
      customerAvatarUrl: null,
    };
  },
  computed: {
    primaryAgent() {
      return has(this.deepConversationData, 'primaryAgent.id')
        ? this.deepConversationData.primaryAgent
        : null;
    },
    assignedAgents() {
      return get(this.deepConversationData, 'agents', [])
        .filter(({ id, email }) => email !== BOT_EMAIL && id !== get(this.primaryAgent, 'id'));
    },
    isLocked() {
      return has(this.deepConversationData, 'locked_by.id');
    },
    lockStatus() {
      const title = this.isLocked ? 'Locked' : 'Unlocked';
      const prefix = `Conversation ${title.toLowerCase()} by`;
      const message = this.findSystemMessage(prefix);
      return {
        at: this.formatDate(get(message, 'created_at', get(this.deepConversationData, 'updated_at'))),
        icon: this.isLocked ? 'lock-keyhole' : 'unlock-keyhole',
        isLocked: this.isLocked,
        message: this.displayedSystemMessageContent(message),
        title,
      };
    },
    lastSenderInfo() {
      const botId = get(this.$store.getters['conversations/bot'], 'id');
      const senderId = get(this.shallowConversationData, 'latest_message.sender_id');
      const senderType = get(this.shallowConversationData, 'latest_message.sender_type');
      if (!senderType) return null;

      if (senderId === botId) {
        return {
          name: this.LAST_SENDER_TYPES.bot,
          role: this.LAST_SENDER_TYPES.bot,
        };
      }

      if (senderType === 'agents') {
        const agent = (get(this.deepConversationData, 'agents', []) || [])
          .find(({ id }) => id === senderId);

        return {
          avatarKey: get(agent, 'id'),
          avatarUrl: agent ? this.agentAvatarUrl(agent) : '',
          name: get(agent, 'fullName', this.LAST_SENDER_TYPES.agents),
          role: this.LAST_SENDER_TYPES.agents,
        };
      }

      if (senderType === 'customers') {
        const customer = get(this.deepConversationData, 'customer', {});
        return {
          avatarKey: get(customer, 'id'),
          avatarUrl: this.customerAvatarUrl,
          name: this.customerName(customer),
          role: this.LAST_SENDER_TYPES.customers,
        };
      }

      return {
        name: this.LAST_SENDER_TYPES[senderType] || senderType,
        role: this.LAST_SENDER_TYPES[senderType],
      };
    },
    LAST_SENDER_TYPES() {
      return {
        agents: 'Agent',
        bot: 'Bot',
        customers: this.$t(`${this.baseTranslationPath}.last_sender_type.customer`),
        system: 'System',
      };
    },
    baseTranslationPath() {
      return 'conversation_tab.left_panel.hover_popup';
    },
    lastUpdated() {
      return this.formatDate(get(this.deepConversationData, 'updated_at')) || 'N/A';
    },
    isActive() {
      return get(this.deepConversationData, 'status') === 'active';
    },
    statusLabel() {
      const status = get(this.deepConversationData, 'status', 'N/A');
      return status === 'N/A' ? status : `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
    },
    lastMessagePreview() {
      return this.buildMessagePreview(get(this.latestMessage, 'payload.content', ''));
    },
    latestMessage() {
      return get(this.shallowConversationData, 'latest_message', {});
    },
    latestMessageType() {
      return get(this.latestMessage, 'payload.message_type');
    },
    shouldRenderLatestMessageHtml() {
      return isUndefined(this.latestMessageType);
    },
    shouldRenderLatestMessageReplies() {
      return this.latestMessageType === 'replies';
    },
    shouldRenderLatestMessageSelectedReply() {
      return this.latestMessageType === 'reply';
    },
    shouldRenderLatestMessageCall() {
      return this.latestMessageType === 'call';
    },
    shouldRenderLatestMessageCarousel() {
      return this.latestMessageType === 'carousel';
    },
    shouldRenderLatestMessageRichText() {
      return this.latestMessageType === 'rich_text';
    },
    latestMessageRepliesContent() {
      const messages = get(this.deepConversationData, 'messages', []);
      const repliesMsgIdx = messages.findIndex(m => m.id === get(this.latestMessage, 'id'));
      const contentMessage = repliesMsgIdx !== -1 ? messages[repliesMsgIdx + 1] : null;
      return get(contentMessage, 'payload.content', '');
    },
    latestMessageReplies() {
      const replies = get(this.latestMessage, 'payload.replies.attachments', []);
      return replies.map(reply => get(reply, 'reply.content'));
    },
    latestMessageSelectedReplyContent() {
      const messages = get(this.deepConversationData, 'messages', []);
      const originalRepliesMsgId = get(this.latestMessage, 'payload.reply.postback.reply_to');
      const originalRepliesMsgIdx = get(this.deepConversationData, 'messages', []).findIndex(m => m.id === originalRepliesMsgId);
      const contentMessage = originalRepliesMsgIdx !== -1 ? messages[originalRepliesMsgIdx + 1] : null;
      return get(contentMessage, 'payload.content', '');
    },
    latestMessageSelectedReply() {
      const reply = get(this.latestMessage, 'payload.reply.content', '');
      return [reply];
    },
    latestMessageCall() {
      const callType = get(this.latestMessage, 'payload.call.type', 'video');
      const callDuration = get(this.latestMessage, 'payload.call.duration');
      const callDurationFormatted = callDuration ? `(Lasted ${this.$aiq.formatters.timeDelta(callDuration)})` : '';
      return `${this.$t(`${this.baseTranslationPath}.${callType}`)} ${callDurationFormatted}`;
    },
    latestMessageCarousel() {
      return this.$t(`${this.baseTranslationPath}.carousel_message`);
    },
    latestMessageRichTextContent() {
      return this.buildMessagePreview(get(this.latestMessage, 'payload.rich_text.content', ''));
    },
    latestMessageRichTextAttachments() {
      const attachments = get(this.latestMessage, 'payload.rich_text.attachments', []) || [];
      const files = attachments.map(a => get(a, 'file')).filter(Boolean);
      const attachedImageCount = files.filter(file => (file.mime || '').toLowerCase().startsWith('image/')).length;
      const imageCount = this.latestMessageRichTextContent.imageCount + attachedImageCount;
      const fileCount = files.length - attachedImageCount;
      let icon = imageCount ? 'image' : 'file-lines';
      if (imageCount && fileCount) icon = 'paperclip';

      return {
        count: imageCount + fileCount,
        icon,
      };
    },
    summary() {
      return get(this.deepConversationData, 'summary.summary', '');
    },
    tags() {
      return get(this.deepConversationData, 'tags', []) || [];
    },
  },
  watch: {
    'deepConversationData.customer': {
      immediate: true,
      async handler(customer) {
        await this.setCustomerAvatarUrl(customer);
      },
    },
  },
  methods: {
    buildMessagePreview(content) {
      const template = document.createElement('template');
      template.innerHTML = this.$filters.sanitize(content);
      const images = template.content.querySelectorAll('img');
      images.forEach(image => image.replaceWith(''));

      return {
        content: template.innerHTML,
        imageCount: images.length,
      };
    },
    agentAvatarUrl(agent) {
      return get(agent, 'picture.payload.thumbnail.url', DEFAULT_PICTURE_URL);
    },
    async setCustomerAvatarUrl(customer) {
      const filePayload = get(customer, 'profile_image.payload.thumbnail');
      const url = await this.getFileUrl(filePayload);
      this.customerAvatarUrl = url || DEFAULT_PICTURE_URL;
    },
    customerName(customer) {
      const customerId = get(customer, 'id', get(this.shallowConversationData, 'customer_id'));
      const displayName = get(customer, 'profile.displayName', '');
      const firstName = get(customer, 'profile.first_name', '');
      const lastName = get(customer, 'profile.last_name', '');
      const customerName = `${firstName} ${lastName}`.trim() || displayName;

      return customerName
        || this.$t('conversation_tab.left_panel.conversation_item.default_title', { customer_id: customerId });
    },
    formatDate(timestamp) {
      if (!timestamp) return '';

      return dayjs(timestamp).format('MMMM D, YYYY [at] h:mm A');
    },
    findSystemMessage(prefix) {
      return [...(get(this.deepConversationData, 'messages', []) || [])]
        .reverse()
        .find((message) => {
          const isSystemMessage = get(message, 'payload.message_type') === 'system';

          return isSystemMessage && this.systemMessageContent(message).startsWith(prefix);
        });
    },
    systemMessageContent(message) {
      return get(message, 'payload.system.content', '');
    },
    displayedSystemMessageContent(message) {
      const content = get(message, 'payload.system.content', '');
      return upperFirst(content.replace(/^Conversation\s+/, ''));
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/style-guide-light-theme.scss';
@import '@/styles/aiq-mixins.scss';

.tooltip-content-container {
  color: $aiq-theme-text-primary;
  font-size: 15px;
  line-height: 1.45;
}

.top {
  display: flex;
  min-width: 0;
}

.panel {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  padding: 24px 16px 16px;

  &:first-child {
    border-right: 1px solid $aiq-theme-border-default;
  }
}

.header {
  align-items: center;
  color: $aiq-theme-text-primary;
  display: flex;
  gap: 8px;
  margin-bottom: 24px;

  span {
    font-size: 16px;
    font-weight: 600;
  }

  svg {
    font-size: 16px;
  }
}

.field-group {
  margin-bottom: 24px;
}

.field-label {
  color: $aiq-theme-text-primary;
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
}

.field-value,
.muted-value {
  color: $aiq-theme-text-primary;
  font-size: 14px;
}

.field-value.latest-message,
.rich-text-content {
  @include list-reset;
  display: -webkit-box;
  overflow: hidden;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.last-message-preview {
  align-items: center;
  display: flex;
  gap: 8px;

  .latest-message,
  .rich-text-content {
    flex: 1;
    min-width: 0;
  }
}

.last-message-attachment-count {
  align-items: center;
  color: $aiq-theme-text-muted;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 14px;
  gap: 6px;
  white-space: nowrap;
}

.field-value.last-updated {
  margin-left: 8px;
}

.latest-message-replies {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  margin-left: 8px;
  margin-right: 8px;
}

.latest-message-reply {
  background: $aiq-theme-background-elevated;
  border-radius: 999px;
  color: $aiq-theme-text-primary;
  display: inline-flex;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  padding: 12px 16px;
}

.tooltip-content-message {
  margin-top: 0;
}

.agent-row {
  align-items: center;
  display: flex;
  gap: 8px;
  min-width: 0;
  width: 100%;
  margin-bottom: 4px;
  margin-left: 8px;
}

.agent-avatar {
  border-radius: 50%;
  flex: 0 0 28px;
  height: 28px;
  overflow: hidden;
  width: 28px;
}

.sender-avatar-icon {
  align-items: center;
  color: $aiq-theme-text-muted;
  display: inline-flex;
  justify-content: center;

  svg {
    font-size: 28px;
  }
}

.agent-name {
  color: $aiq-theme-text-primary;
  font-size: 14px;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.agent-role {
  color: $aiq-theme-text-muted;
  font-size: 14px;
  white-space: nowrap;
}

.lock-status {
  margin-top: 28px;
}

.lock-status-title {
  align-items: center;
  color: $aiq-theme-semantic-error;
  display: flex;
  gap: 8px;
  margin-bottom: 4px;

  span {
    font-size: 16px;
    font-weight: 500;
  }
  svg {
    font-size: 16px;
  }
}

.lock-status.unlocked .lock-status-title {
  color: $aiq-theme-text-muted;
}

.lock-status-line {
  font-size: 14px;
  margin-left: 8px;
  margin-top: 8px;
}

.status-row {
  align-items: center;
  display: flex;
  gap: 16px;

  .field-label {
    margin-bottom: 0;
  }
}

.status-badge {
  background: rgba($aiq-theme-semantic-success, 0.25);
  border-radius: 999px;
  display: inline-flex;
  padding: 4px 12px;
  font-weight: 500;

  &.inactive {
    background: rgba($aiq-theme-semantic-error, 0.25);
  }
}

.conversation-context-container {
  border-top: 1px solid $aiq-theme-border-default;
  padding: 20px 16px 16px;
}

.summary-text {
  display: -webkit-box;
  font-size: 14px;
  overflow: hidden;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;

  &.summary-unavailable {
    color: $aiq-theme-text-muted;
  }
}

.tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  border: 1px solid $aiq-theme-text-secondary;
  color: $aiq-theme-text-secondary;
  background: $aiq-theme-background-surface;
  display: inline-flex;
  font-size: 14px;
  line-height: 1;
  padding: 8px 12px;
}
</style>

<style lang="scss">
@import '@/styles/style-guide-light-theme.scss';

.conversation-item-tooltip-content.el-popper {
  padding: 0;
  width: 720px;
  border: 1px solid $aiq-theme-border-default;
  border-radius: 4px;
  box-shadow: 4px 4px 4px 0 #2b3b4540;

  & .el-popper__arrow::before {
    border-color: $aiq-theme-border-default;
  }
}
</style>
